/**
 * Existing-table Target-step commit (S6.T2c-1) — the `commitTargetAction`
 * branch for `mode === 'existing-table'`, extracted to a sibling module so
 * `dataImportStore.actions.ts` (already 217 lines pre-existing) doesn't grow
 * further (CLAUDE.md ≤200-line rule) — mirrors the `dataImportStore.columnActions.ts`
 * split.
 *
 * Mirrors the new-table branch's shape (`fetchNativeTypes` → `commitTargetAction`
 * in `dataImportStore.actions.ts`): a rejection-safe IPC wrapper
 * (`fetchTargetColumnsForCommit`) feeding ONE revision-aware `applyResult`
 * transition (CP3: "schema fetch + auto-match + projection + blocker recompute
 * in ONE transition" — here there's no projection step; native types come from
 * the target table's own facts, not a projected default). `current.columns` is
 * used AS-IS (no `applyOverrides` re-derivation): unlike new-table mode,
 * existing-table mode introduces no fresh per-column data that needs folding
 * in — `columns` is already kept fresh by whichever prior action (analysis
 * ingestion / column override) last touched it. This also sidesteps a circular
 * import: `applyOverrides` lives in `dataImportStore.actions.ts`, which is the
 * file that calls into this one.
 */
import { reconcileSuggestedMappings } from '../mapping/reconcileSuggestedMappings';
import { validateMapping } from '../mapping/columnMapping';
import type { ImportTargetColumnsRequest, ImportTargetColumnsResponse, ImportTargetPlan } from '../../../../types/electron';
import type { DataImportEntry } from './dataImportStore';

/** Shown inline when the commit-time `fetchTargetColumns` call fails or rejects —
 *  mirrors `dataImportStore.actions.ts`'s `PROJECT_TYPES_ERROR_MESSAGE` contract. */
export const TARGET_COLUMNS_ERROR_MESSAGE = "Couldn't read the destination table's columns. Try again.";

/** `fetchTargetColumns` wrapped so a rejection never becomes an unhandled promise —
 *  mirrors `dataImportStore.actions.ts`'s `fetchNativeTypes`. */
async function fetchTargetColumnsForCommit(
  req: ImportTargetColumnsRequest,
): Promise<ImportTargetColumnsResponse | undefined> {
  try {
    return await window.electronAPI?.dataImport.fetchTargetColumns(req);
  } catch {
    return undefined;
  }
}

/**
 * True when `current`'s active draft (or, once committed, its target) still
 * points at the SAME destination this request was fired for. A newer edit
 * (`setDraftTarget` changing schema/table while this fetch was in flight, or a
 * second commit that's already landed) makes an in-flight response stale —
 * dropped rather than clobbering whatever the newer request already applied.
 */
function isSameRequest(current: DataImportEntry, requested: Partial<ImportTargetPlan> & { connectionId: string }): boolean {
  const active = current.draftTarget ?? current.target;
  return (
    active?.connectionId === requested.connectionId &&
    active?.database === requested.database &&
    active?.schema === requested.schema &&
    active?.table === requested.table
  );
}

/**
 * `commitTargetAction`'s existing-table branch body. `draft` is the ALREADY
 * connection-resolvable draft (`canResolveEngine`'s guard, applied by the
 * caller before dispatching here). `applyResult` re-reads the FRESHEST entry
 * when it fires — same "don't clobber a concurrent edit" contract the
 * new-table branch follows.
 */
export async function commitExistingTableTarget(
  entry: DataImportEntry,
  draft: Partial<ImportTargetPlan> & { connectionId: string },
  applyResult: (build: (current: DataImportEntry) => DataImportEntry) => void,
): Promise<void> {
  const analysisId = entry.analysis?.analysisId;
  const { database, table } = draft;
  // sqlite has no schema and mysql treats it as a database synonym — both commit
  // with an empty/absent schema (targetStepGate only requires it when schemaRequired,
  // and gates the CTA on that). Coerce to '' so the request is always well-typed;
  // never gate the fetch on schema itself.
  const schema = draft.schema ?? '';

  // Precondition failure (no analyzed file yet, or database/table not chosen) —
  // surfaces the same inline error a fetch failure would, rather than silently
  // no-op-ing (the CTA gate normally prevents reaching here without these set).
  if (!analysisId || !database || !table) {
    applyResult((current) => ({ ...current, targetColumnsError: TARGET_COLUMNS_ERROR_MESSAGE }));
    return;
  }

  const response = await fetchTargetColumnsForCommit({
    analysisId,
    connectionId: draft.connectionId,
    database,
    schema,
    table,
  });

  if (!response) {
    applyResult((current) =>
      isSameRequest(current, draft) ? { ...current, targetColumnsError: TARGET_COLUMNS_ERROR_MESSAGE } : current,
    );
    return;
  }
  if (!response.success) {
    applyResult((current) =>
      isSameRequest(current, draft)
        ? { ...current, targetColumnsError: response.error || TARGET_COLUMNS_ERROR_MESSAGE }
        : current,
    );
    return;
  }

  applyResult((current) => {
    // A newer commit has since superseded this one — drop the stale response outright.
    if (!isSameRequest(current, draft)) return current;

    // Freshest columns as of this transition (already overrides-applied by
    // whichever action last touched them) — never re-derived here.
    const sourceColumns = current.columns;
    const reconciledMappings = reconcileSuggestedMappings(response.suggestedMappings, sourceColumns);
    const issues = validateMapping(reconciledMappings, sourceColumns, response.columns);
    const includedCount = sourceColumns.filter((c) => c.include).length;

    return {
      ...current,
      target: { ...draft, mode: 'existing-table' } as ImportTargetPlan,
      draftTarget: undefined,
      // New-table-only concept — clears any stale projection (+ its error) left
      // over from a prior new-table commit on this same tab (a mode flip must
      // leave no stale native-type state).
      projectedNativeTypes: undefined,
      projectTypesError: null,
      targetColumns: response.columns,
      existingRowCount: response.existingRowCount,
      targetColumnsError: undefined,
      mappings: reconciledMappings,
      // The re-match toast announces that a destination EDIT re-applied the user's column
      // work — on the FIRST commit (no prior target) nothing was re-matched, the rail and
      // blocker strips already present the fresh state, and the toast only added noise on
      // arrival at Columns (the DI-1 screenshot-audit finding).
      rematch: current.target
        ? {
            matched: reconciledMappings.length,
            total: includedCount,
            newBlockers: issues.filter((i) => i.kind === 'unmapped-required').length,
          }
        : null,
    };
  });
}
